import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
  InputAdornment,
  Snackbar,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Insights as InsightsIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as InactiveIcon,
  Category as CategoryIcon,
  Refresh,
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/currency';
import { productService, Product } from '../../services/productService';
import { categoryService, Category } from '../../services/categoryService';
import { RouterLink } from '../../utils/linkNav';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  TypePill,
  StatusDot,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';

const ProductManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [typeFilter, setTypeFilter] = useState('');
  const [capFilter, setCapFilter] = useState<'all' | 'stockable' | 'purchasable' | 'saleable'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch data
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  // Filter products based on search and filters
  useEffect(() => {
    let filtered = products;

    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.product_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.hsn_code && product.hsn_code.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(product => product.category === categoryFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(product =>
        statusFilter === 'active' ? product.is_active : !product.is_active
      );
    }

    if (typeFilter) {
      filtered = filtered.filter(product => product.product_type === typeFilter);
    }

    if (capFilter !== 'all') {
      const key = capFilter === 'stockable' ? 'can_stock' : capFilter === 'purchasable' ? 'can_purchase' : 'can_sell';
      filtered = filtered.filter(product => Number((product as any)[key]) === 1);
    }

    setFilteredProducts(filtered);
  }, [products, searchTerm, categoryFilter, statusFilter, typeFilter, capFilter]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getProducts();
      setProducts(response.products);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getCategories();
      setCategories(response.categories);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleToggleStatus = async (product: Product) => {
    try {
      await productService.toggleStatus(product.id);
      setSnackbar({
        open: true,
        message: `Product ${product.is_active ? 'deactivated' : 'activated'} successfully`,
        severity: 'success'
      });
      await fetchProducts();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update product status';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const hasFilters = !!(searchTerm || categoryFilter || statusFilter !== 'all' || typeFilter || capFilter !== 'all');
  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setStatusFilter('all');
    setTypeFilter('');
    setCapFilter('all');
  };

  const typeOptions = Array.from(new Set(products.map((p) => p.product_type).filter(Boolean))) as string[];
  const prettyType = (t?: string) => (t ? t.replace(/_/g, ' ') : '—');

  const toggleSelect = (id: string) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const allVisibleSelected = filteredProducts.length > 0 && filteredProducts.every((p) => selected.has(p.id));
  const toggleSelectAll = () => setSelected(allVisibleSelected ? new Set() : new Set(filteredProducts.map((p) => p.id)));

  const bulkSetStatus = async (active: boolean) => {
    if (!selected.size) return;
    try {
      await productService.bulkStatus(Array.from(selected), active);
      setSnackbar({ open: true, message: `${selected.size} product(s) ${active ? 'activated' : 'deactivated'}`, severity: 'success' });
      setSelected(new Set());
      await fetchProducts();
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Bulk update failed', severity: 'error' });
    }
  };

  // Stat cards computed client-side from the already-fetched lists -- null
  // while loading so a slow fetch never briefly reads as "zero products".
  const totalCount = loading ? null : products.length;
  const activeCount = loading ? null : products.filter(p => p.is_active).length;
  const inactiveCount = loading ? null : products.filter(p => !p.is_active).length;
  const categoryCount = loading ? null : categories.length;

  return (
    <Box>
      <PageHeader
        title="Product Management"
        subtitle="Manage the product catalogue, HSN mapping and GST rates"
        actions={
          <>
            <IconButton onClick={fetchProducts} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              component={RouterLink}
              to="/sales/products/new"
            >
              Add New Product
            </Button>
          </>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard title="Total Products" value={totalCount} icon={<InventoryIcon />} color="#2563eb" />
        <StatCard title="Active" value={activeCount} icon={<CheckCircleIcon />} color="#059669" />
        <StatCard title="Inactive" value={inactiveCount} icon={<InactiveIcon />} color="#64748b" />
        <StatCard title="Categories" value={categoryCount} icon={<CategoryIcon />} color="#7c3aed" />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          placeholder="Search by name, code, HSN..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 190 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Categories</MenuItem>
          {categories.map(category => (
            <MenuItem key={category.id} value={category.name}>{category.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All Types</MenuItem>
          {typeOptions.map((t) => <MenuItem key={t} value={t}>{prettyType(t)}</MenuItem>)}
        </TextField>
        <TextField
          select
          size="small"
          label="Capability"
          value={capFilter}
          onChange={(e) => setCapFilter(e.target.value as any)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="stockable">Stockable</MenuItem>
          <MenuItem value="purchasable">Purchasable</MenuItem>
          <MenuItem value="saleable">Saleable</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Products</MenuItem>
          <MenuItem value="active">Active Only</MenuItem>
          <MenuItem value="inactive">Inactive Only</MenuItem>
        </TextField>

        <FilterSpacer />

        {selected.size > 0 && (
          <>
            <Button size="small" variant="outlined" color="success" onClick={() => bulkSetStatus(true)}>Activate ({selected.size})</Button>
            <Button size="small" variant="outlined" color="warning" onClick={() => bulkSetStatus(false)}>Deactivate ({selected.size})</Button>
          </>
        )}

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
        <Typography variant="body2" color="text.secondary">
          {filteredProducts.length} of {products.length} products
        </Typography>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Products Table */}
      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={headerCellSx}>
                  <Checkbox size="small" checked={allVisibleSelected} indeterminate={selected.size > 0 && !allVisibleSelected} onChange={toggleSelectAll} />
                </TableCell>
                <TableCell sx={headerCellSx}>Product Code</TableCell>
                <TableCell sx={headerCellSx}>Product Name</TableCell>
                <TableCell sx={headerCellSx}>Type</TableCell>
                <TableCell sx={headerCellSx}>HSN Code</TableCell>
                <TableCell sx={headerCellSx}>Category</TableCell>
                <TableCell sx={headerCellSx}>Unit</TableCell>
                <TableCell sx={headerCellSx} align="right">Base Price</TableCell>
                <TableCell sx={headerCellSx} align="right">Purchase Price</TableCell>
                <TableCell sx={headerCellSx} align="center">GST Rate</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={12} sx={{ border: 0 }}>
                    <ErrorState message={error} onRetry={fetchProducts} />
                  </TableCell>
                </TableRow>
              ) : filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No products found"
                      hint={hasFilters ? 'Try adjusting your search criteria' : 'Start by creating your first product'}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((product) => (
                  <TableRow key={product.id} sx={bodyRowSx} hover selected={selected.has(product.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selected.has(product.id)} onChange={() => toggleSelect(product.id)} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <CodeCell code={product.product_code} tint="#2563eb" icon={<InventoryIcon fontSize="small" />} />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {product.product_name}
                        </Typography>
                        {product.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {product.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {product.product_type ? (
                        <TypePill label={prettyType(product.product_type)} tint="#0891b2" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {product.hsn_code ? (
                        <Typography variant="body2" fontFamily="monospace">
                          {product.hsn_code}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {product.category ? (
                        <TypePill label={product.category} tint="#7c3aed" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{product.unit_of_measure}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(product.base_price || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrency(product.purchase_price || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <TypePill label={`${product.gst_rate}%`} tint="#d97706" />
                    </TableCell>
                    <TableCell>
                      <Box
                        onClick={() => handleToggleStatus(product)}
                        title="Click to toggle status"
                        sx={{ cursor: 'pointer', display: 'inline-flex' }}
                      >
                        <StatusDot label={product.is_active ? 'Active' : 'Inactive'} tone={product.is_active ? 'success' : 'neutral'} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          component={RouterLink}
                          to={`/sales/products/${product.id}/edit`}
                          title="Edit Product"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          component={RouterLink}
                          to={`/sales/products/${product.id}/360`}
                          title="View 360"
                        >
                          <InsightsIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductManagementPage;
